// TEMPORÁRIO — mock local do Portal do Credenciado, remover no desenvolvimento do backend.
import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { paginar, type Pagina } from '@/modulos/portal-credenciado/comum/contrato';
import { resolverMock } from '@/modulos/portal-credenciado/comum/mock';

import type { FiltroDoExtrato, LancamentoDoExtrato } from './contrato';
import dadosMock from './dados.mock.json';

/**
 * Camada de dados do Extrato Consolidado do Portal do Credenciado.
 *
 * Mesmo desenho das demais telas do módulo: nenhum componente busca dado
 * direto, a chave de cache mora junto de quem a usa, e o `queryFn` resolve
 * `dados.mock.json` em vez de chamar `chamarApi('/credenciado/...')` — ver a
 * justificativa em `comum/mock.ts`.
 */
export const CHAVES_DO_EXTRATO = {
  lancamentos: (filtro: FiltroDoExtrato) =>
    ['credenciado', 'extrato', 'lancamentos', filtro] as const,
};

const LANCAMENTOS_MOCK = dadosMock.lancamentos as LancamentoDoExtrato[];

function lancamentosFiltrados(competencia?: string): LancamentoDoExtrato[] {
  return competencia
    ? LANCAMENTOS_MOCK.filter((lancamento) => lancamento.data.slice(0, 7) === competencia)
    : LANCAMENTOS_MOCK;
}

/**
 * O extrato, paginado no mesmo formato que a API real vai devolver
 * (`comum/contrato.ts`). O filtro por competência acontece antes da
 * paginação — igual a um `WHERE` que roda antes do `LIMIT/OFFSET` no
 * servidor. O saldo acumulado de cada linha é o saldo real da conta até
 * aquela data, não recalculado a partir do recorte filtrado.
 */
export function useLancamentosDoExtrato(
  filtro: FiltroDoExtrato,
): UseQueryResult<Pagina<LancamentoDoExtrato>, Error> {
  return useQuery<Pagina<LancamentoDoExtrato>, Error>({
    queryKey: CHAVES_DO_EXTRATO.lancamentos(filtro),
    queryFn: () =>
      resolverMock(
        paginar(lancamentosFiltrados(filtro.competencia), filtro.pagina, filtro.tamanho),
      ),
  });
}
